<template>
  <div class="historial-ventas">
    <div class="page-header">
      <div class="header-content">
        <h1>
          <i class="bi bi-clock-history"></i>
          Historial de Ventas
        </h1>
        <p class="subtitle">Gestiona todas tus facturas electrónicas emitidas</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="refreshFacturas">
          <i class="bi bi-arrow-clockwise"></i>
          Actualizar
        </button>
        <router-link to="/ventas/facturacion" class="btn btn-success">
          <i class="bi bi-plus-circle"></i>
          Nueva Factura
        </router-link>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Buscar</label>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Número, cliente, RUC..."
            class="form-control"
            @input="applyFilters"
          >
        </div>
        <div class="filter-group">
          <label>Estado</label>
          <select v-model="filters.estado" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="draft">Borrador</option>
            <option value="generated">Generada</option>
            <option value="issued">Autorizada</option>
            <option value="rejected">Rechazada</option>
            <option value="cancelled">Anulada</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Desde</label>
          <input 
            v-model="filters.fechaDesde" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          >
        </div>
        <div class="filter-group">
          <label>Hasta</label>
          <input 
            v-model="filters.fechaHasta" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          >
        </div>
        <div class="filter-group filter-actions">
          <button @click="clearFilters" class="btn btn-outline-secondary">
            <i class="bi bi-x-circle"></i>
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla de Facturas -->
    <div class="facturas-table-card">
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p>Cargando facturas...</p>
      </div>

      <div v-else-if="facturasFiltradas.length === 0" class="empty-state">
        <i class="bi bi-inbox"></i>
        <h3>No hay facturas</h3>
        <p>No se encontraron facturas con los filtros aplicados</p>
        <button @click="clearFilters" class="btn btn-primary">
          Limpiar Filtros
        </button>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Número</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>RUC/CI</th>
              <th class="text-end">Total</th>
              <th>Estado</th>
              <th>Autorización SRI</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="factura in facturasFiltradas" :key="factura.id">
              <td>
                <router-link
                  v-if="factura.status === 'draft'"
                  :to="`/ventas/facturacion?edit=${factura.id}`"
                  class="factura-numero-link"
                  title="Editar borrador"
                >
                  <strong>{{ formatNumeroFactura(factura) }}</strong>
                </router-link>
                <a
                  v-else
                  href="#"
                  class="factura-numero-link"
                  @click.prevent="verDetalle(factura)"
                  title="Ver detalle"
                >
                  <strong>{{ formatNumeroFactura(factura) }}</strong>
                </a>
              </td>
              <td>
                {{ formatDate(factura.fecha_emision) }}
              </td>
              <td>
                <div class="cliente-info">
                  <strong>{{ factura.cliente_razon_social }}</strong>
                  <small v-if="factura.cliente_email">{{ factura.cliente_email }}</small>
                </div>
              </td>
              <td>{{ factura.cliente_identificacion }}</td>
              <td class="text-end">
                <strong>${{ formatMoney(factura.total) }}</strong>
              </td>
              <td>
                <span :class="['badge', getEstadoBadgeClass(factura.status)]">
                  {{ getEstadoLabel(factura.status) }}
                </span>
              </td>
              <td>
                <div v-if="factura.numero_autorizacion_sri" class="autorizacion-info">
                  <small class="text-muted">{{ factura.numero_autorizacion_sri }}</small>
                  <small class="text-muted d-block">
                    {{ formatDate(factura.fecha_autorizacion_sri) }}
                  </small>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <div class="action-buttons">
                  <!-- Ver Detalle -->
                  <button 
                    @click="verDetalle(factura)" 
                    class="btn btn-sm btn-outline-primary"
                    title="Ver detalle"
                  >
                    <i class="bi bi-eye"></i>
                  </button>

                  <!-- Editar (solo borradores) -->
                  <button 
                    v-if="factura.status === 'draft'"
                    @click="editarFactura(factura)" 
                    class="btn btn-sm btn-outline-warning"
                    title="Editar"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>

                  <!-- Firmar y Enviar (borradores o generadas) -->
                  <button 
                    v-if="['draft', 'generated'].includes(factura.status)"
                    @click="firmarYEnviar(factura)" 
                    class="btn btn-sm btn-outline-success"
                    title="Firmar y enviar al SRI"
                  >
                    <i class="bi bi-send"></i>
                  </button>

                  <!-- Ver XML (facturas autorizadas) -->
                  <button 
                    v-if="factura.status === 'issued' && factura.xml_firmado"
                    @click="descargarXML(factura)" 
                    class="btn btn-sm btn-outline-info"
                    title="Descargar XML"
                  >
                    <i class="bi bi-file-earmark-code"></i>
                  </button>

                  <!-- Ver PDF/RIDE -->
                  <button 
                    @click="descargarPDF(factura)" 
                    class="btn btn-sm btn-outline-danger"
                    title="Descargar RIDE (PDF)"
                    :disabled="!puedeGenerarRIDE(factura)"
                  >
                    <i class="bi bi-file-pdf"></i>
                  </button>

                  <!-- Reenviar al SRI (facturas rechazadas) -->
                  <button 
                    v-if="factura.status === 'rejected'"
                    @click="reenviarSRI(factura)" 
                    class="btn btn-sm btn-outline-warning"
                    title="Reenviar al SRI"
                  >
                    <i class="bi bi-arrow-repeat"></i>
                  </button>

                  <!-- Anular (solo autorizadas) -->
                  <button 
                    v-if="factura.status === 'issued'"
                    @click="anularFactura(factura)" 
                    class="btn btn-sm btn-outline-danger"
                    title="Anular factura"
                  >
                    <i class="bi bi-x-circle"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal Detalle Factura -->
    <div v-if="showDetalleModal" class="modal-overlay" @click.self="closeDetalleModal">
      <div class="modal-content modal-lg">
        <div class="modal-header">
          <h3>
            <i class="bi bi-file-text"></i>
            Detalle de Factura
          </h3>
          <button @click="closeDetalleModal" class="btn-close">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body" v-if="facturaSeleccionada">
          <div class="factura-detalle">
            <!-- Encabezado -->
            <div class="detalle-section">
              <h4>Información General</h4>
              <div class="info-grid">
                <div class="info-item">
                  <label>Número:</label>
                  <strong>{{ formatNumeroFactura(facturaSeleccionada) }}</strong>
                </div>
                <div class="info-item">
                  <label>Fecha Emisión:</label>
                  <span>{{ formatDate(facturaSeleccionada.fecha_emision) }}</span>
                </div>
                <div class="info-item">
                  <label>Estado:</label>
                  <span :class="['badge', getEstadoBadgeClass(facturaSeleccionada.status)]">
                    {{ getEstadoLabel(facturaSeleccionada.status) }}
                  </span>
                </div>
                <div class="info-item" v-if="facturaSeleccionada.clave_acceso">
                  <label>Clave de Acceso:</label>
                  <code>{{ facturaSeleccionada.clave_acceso }}</code>
                </div>
              </div>
            </div>

            <!-- Cliente -->
            <div class="detalle-section">
              <h4>Datos del Cliente</h4>
              <div class="info-grid">
                <div class="info-item">
                  <label>Razón Social:</label>
                  <span>{{ facturaSeleccionada.cliente_razon_social }}</span>
                </div>
                <div class="info-item">
                  <label>Identificación:</label>
                  <span>{{ facturaSeleccionada.cliente_identificacion }}</span>
                </div>
                <div class="info-item">
                  <label>Email:</label>
                  <span>{{ facturaSeleccionada.cliente_email || 'No especificado' }}</span>
                </div>
                <div class="info-item">
                  <label>Teléfono:</label>
                  <span>{{ facturaSeleccionada.cliente_telefono || 'No especificado' }}</span>
                </div>
              </div>
            </div>

            <!-- Items -->
            <div class="detalle-section">
              <h4>Productos/Servicios</h4>
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-center">Cantidad</th>
                    <th class="text-end">P. Unit.</th>
                    <th class="text-end">Descuento</th>
                    <th class="text-end">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in facturaSeleccionada.items" :key="idx">
                    <td>{{ item.description || item.descripcion }}</td>
                    <td class="text-center">{{ item.quantity || item.cantidad }}</td>
                    <td class="text-end">${{ formatMoney(item.precio_unitario) }}</td>
                    <td class="text-end">${{ formatMoney(item.descuento) }}</td>
                    <td class="text-end">${{ formatMoney(item.total_linea) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totales -->
            <div class="detalle-section">
              <h4>Totales</h4>
              <div class="totales-grid">
                <div class="total-row">
                  <span>Subtotal:</span>
                  <strong>${{ formatMoney(facturaSeleccionada.subtotal) }}</strong>
                </div>
                <div class="total-row" v-if="facturaSeleccionada.descuento_total > 0">
                  <span>Descuento:</span>
                  <strong>-${{ formatMoney(facturaSeleccionada.descuento_total) }}</strong>
                </div>
                <div class="total-row">
                  <span>IVA:</span>
                  <strong>${{ formatMoney(facturaSeleccionada.iva) }}</strong>
                </div>
                <div class="total-row total-final">
                  <span>TOTAL:</span>
                  <strong>${{ formatMoney(facturaSeleccionada.total) }}</strong>
                </div>
              </div>
            </div>

            <!-- Información SRI -->
            <div class="detalle-section" v-if="facturaSeleccionada.numero_autorizacion_sri">
              <h4>Información SRI</h4>
              <div class="info-grid">
                <div class="info-item">
                  <label>Número de Autorización:</label>
                  <code>{{ facturaSeleccionada.numero_autorizacion_sri }}</code>
                </div>
                <div class="info-item">
                  <label>Fecha Autorización:</label>
                  <span>{{ formatDateTime(facturaSeleccionada.fecha_autorizacion_sri) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDetalleModal" class="btn btn-secondary">
            Cerrar
          </button>
          <button 
            v-if="facturaSeleccionada && puedeGenerarRIDE(facturaSeleccionada)"
            @click="descargarPDF(facturaSeleccionada)" 
            class="btn btn-danger"
          >
            <i class="bi bi-file-pdf"></i>
            Descargar RIDE
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmar Firma y Envío -->
    <div v-if="showFirmarModal" class="modal-overlay" @click.self="closeFirmarModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            <i class="bi bi-shield-check"></i>
            Firmar y Enviar al SRI
          </h3>
          <button @click="closeFirmarModal" class="btn-close">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info">
            <i class="bi bi-info-circle"></i>
            Esta acción firmará digitalmente la factura y la enviará al SRI para su autorización.
          </div>
          
          <div class="factura-info-box">
            <h5>Factura a enviar:</h5>
            <p><strong>Número:</strong> {{ formatNumeroFactura(facturaSeleccionada) }}</p>
            <p><strong>Cliente:</strong> {{ facturaSeleccionada?.cliente_razon_social }}</p>
            <p><strong>Total:</strong> ${{ formatMoney(facturaSeleccionada?.total) }}</p>
          </div>

          <div class="form-group">
            <label for="certificadoPassword">Contraseña del Certificado Digital:</label>
            <input 
              v-model="certificadoPassword"
              id="certificadoPassword"
              type="password" 
              class="form-control"
              placeholder="Ingrese la contraseña de su certificado"
              @keyup.enter="confirmarFirmarYEnviar"
            >
            <small class="form-text text-muted">
              Si dejó configurada la contraseña en el sistema, este campo es opcional.
            </small>
          </div>

          <div v-if="firmarError" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle"></i>
            {{ firmarError }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeFirmarModal" class="btn btn-secondary" :disabled="firmando">
            Cancelar
          </button>
          <button @click="confirmarFirmarYEnviar" class="btn btn-success" :disabled="firmando">
            <span v-if="firmando">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Procesando...
            </span>
            <span v-else>
              <i class="bi bi-send"></i>
              Firmar y Enviar
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmar Anulación -->
    <div v-if="showAnularModal" class="modal-overlay" @click.self="closeAnularModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            <i class="bi bi-exclamation-triangle"></i>
            Confirmar Anulación
          </h3>
          <button @click="closeAnularModal" class="btn-close">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="bi bi-exclamation-triangle"></i>
            <strong>¡Atención!</strong> Esta acción no se puede deshacer.
          </div>
          
          <p>¿Está seguro que desea anular la siguiente factura?</p>
          
          <div class="factura-info-box">
            <p><strong>Número:</strong> {{ formatNumeroFactura(facturaSeleccionada) }}</p>
            <p><strong>Cliente:</strong> {{ facturaSeleccionada?.cliente_razon_social }}</p>
            <p><strong>Total:</strong> ${{ formatMoney(facturaSeleccionada?.total) }}</p>
          </div>

          <div class="form-group">
            <label for="motivoAnulacion">Motivo de Anulación:</label>
            <textarea 
              v-model="motivoAnulacion"
              id="motivoAnulacion"
              class="form-control"
              rows="3"
              placeholder="Ingrese el motivo de la anulación"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAnularModal" class="btn btn-secondary">
            Cancelar
          </button>
          <button @click="confirmarAnular" class="btn btn-danger">
            <i class="bi bi-x-circle"></i>
            Anular Factura
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { showSuccessToast, showErrorToast } from '@/utils/toast'

export default {
  name: 'HistorialVentas',
  
  data() {
    return {
      facturas: [],
      facturasFiltradas: [],
      loading: false,
      
      filters: {
        search: '',
        estado: '',
        fechaDesde: '',
        fechaHasta: ''
      },
      
      showDetalleModal: false,
      showFirmarModal: false,
      showAnularModal: false,
      facturaSeleccionada: null,
      
      certificadoPassword: '',
      firmando: false,
      firmarError: '',
      
      motivoAnulacion: '',
      
      estadosLabels: {
        'draft': 'Borrador',
        'generated': 'Generada',
        'issued': 'Autorizada',
        'rejected': 'Rechazada',
        'cancelled': 'Anulada'
      }
    }
  },
  
  computed: {
    empresaActiva() {
      return this.$store.state.empresaActiva
    }
  },
  
  mounted() {
    this.cargarFacturas()
  },
  
  methods: {
    async cargarFacturas() {
      this.loading = true
      try {
        const response = await api.get('/facturas')
        this.facturas = response.data.data || []
        this.applyFilters()
      } catch (error) {
        console.error('Error al cargar facturas:', error)
        showErrorToast('Error al cargar el historial de facturas')
      } finally {
        this.loading = false
      }
    },
    
    refreshFacturas() {
      this.cargarFacturas()
    },
    
    applyFilters() {
      let filtered = [...this.facturas]
      
      // Filtro por búsqueda
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(f => 
          f.numero_factura?.toLowerCase().includes(search) ||
          f.cliente_razon_social?.toLowerCase().includes(search) ||
          f.cliente_identificacion?.toLowerCase().includes(search) ||
          f.numero_autorizacion_sri?.toLowerCase().includes(search)
        )
      }
      
      // Filtro por estado
      if (this.filters.estado) {
        filtered = filtered.filter(f => f.status === this.filters.estado)
      }
      
      // Filtro por fecha desde
      if (this.filters.fechaDesde) {
        filtered = filtered.filter(f => 
          new Date(f.fecha_emision) >= new Date(this.filters.fechaDesde)
        )
      }
      
      // Filtro por fecha hasta
      if (this.filters.fechaHasta) {
        filtered = filtered.filter(f => 
          new Date(f.fecha_emision) <= new Date(this.filters.fechaHasta)
        )
      }
      
      this.facturasFiltradas = filtered
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        estado: '',
        fechaDesde: '',
        fechaHasta: ''
      }
      this.applyFilters()
    },
    
    verDetalle(factura) {
      this.facturaSeleccionada = factura
      this.showDetalleModal = true
    },
    
    closeDetalleModal() {
      this.showDetalleModal = false
      this.facturaSeleccionada = null
    },
    
    editarFactura(factura) {
      // Redirigir a la vista de edición
      this.$router.push(`/ventas/facturacion?edit=${factura.id}`)
    },
    
    firmarYEnviar(factura) {
      this.facturaSeleccionada = factura
      this.certificadoPassword = ''
      this.firmarError = ''
      this.showFirmarModal = true
    },
    
    closeFirmarModal() {
      this.showFirmarModal = false
      this.facturaSeleccionada = null
      this.certificadoPassword = ''
      this.firmarError = ''
    },
    
    async confirmarFirmarYEnviar() {
      if (this.firmando) return
      
      this.firmando = true
      this.firmarError = ''
      
      try {
        const payload = {}
        if (this.certificadoPassword) {
          payload.certificado_password = this.certificadoPassword
        }
        
        const response = await api.post(
          `/facturas/${this.facturaSeleccionada.id}/firmar-enviar`,
          payload
        )
        
        if (response.data.success) {
          showSuccessToast(
            response.data.message || 'Factura firmada y enviada exitosamente'
          )
          this.closeFirmarModal()
          this.cargarFacturas()
        } else {
          this.firmarError = response.data.error || 'Error al procesar la factura'
        }
      } catch (error) {
        console.error('Error al firmar y enviar:', error)
        this.firmarError = error.response?.data?.error || 'Error al firmar y enviar la factura'
      } finally {
        this.firmando = false
      }
    },
    
    async reenviarSRI(factura) {
      if (!confirm('¿Desea reenviar esta factura al SRI?')) return
      
      try {
        const response = await api.post(`/facturas/${factura.id}/firmar-enviar`)
        
        if (response.data.success) {
          showSuccessToast('Factura reenviada exitosamente')
          this.cargarFacturas()
        }
      } catch (error) {
        console.error('Error al reenviar:', error)
        showErrorToast(error.response?.data?.error || 'Error al reenviar al SRI')
      }
    },
    
    anularFactura(factura) {
      this.facturaSeleccionada = factura
      this.motivoAnulacion = ''
      this.showAnularModal = true
    },
    
    closeAnularModal() {
      this.showAnularModal = false
      this.facturaSeleccionada = null
      this.motivoAnulacion = ''
    },
    
    async confirmarAnular() {
      if (!this.motivoAnulacion.trim()) {
        showErrorToast('Debe ingresar un motivo de anulación')
        return
      }
      
      try {
        await api.delete(`/facturas/${this.facturaSeleccionada.id}`, {
          data: { motivo: this.motivoAnulacion }
        })
        
        showSuccessToast('Factura anulada exitosamente')
        this.closeAnularModal()
        this.cargarFacturas()
      } catch (error) {
        console.error('Error al anular:', error)
        showErrorToast(error.response?.data?.error || 'Error al anular la factura')
      }
    },
    
    async descargarXML(factura) {
      if (!factura.clave_acceso) {
        showErrorToast('Esta factura no tiene clave de acceso')
        return
      }
      
      try {
        const response = await api.get(`/facturas/${factura.id}/xml`, {
          responseType: 'blob'
        })
        
        const blob = new Blob([response.data], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `factura_${factura.clave_acceso}.xml`
        link.click()
        window.URL.revokeObjectURL(url)
        
        showSuccessToast('XML descargado exitosamente')
      } catch (error) {
        console.error('Error al descargar XML:', error)
        showErrorToast('Error al descargar el XML')
      }
    },
    
    async descargarPDF(factura) {
      if (!this.puedeGenerarRIDE(factura)) {
        showErrorToast('Esta factura no está autorizada para generar RIDE')
        return
      }
      
      try {
        const response = await api.get(`/facturas/${factura.id}/pdf`, {
          responseType: 'blob'
        })
        
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `RIDE_${this.formatNumeroFactura(factura)}.pdf`
        link.click()
        window.URL.revokeObjectURL(url)
        
        showSuccessToast('RIDE descargado exitosamente')
      } catch (error) {
        console.error('Error al descargar PDF:', error)
        showErrorToast('Error al generar el RIDE')
      }
    },
    
    puedeGenerarRIDE(factura) {
      return factura.status === 'issued' && factura.numero_autorizacion_sri
    },
    
    formatNumeroFactura(factura) {
      if (!factura) return ''
      return `${factura.establecimiento}-${factura.punto_emision}-${String(factura.secuencial).padStart(9, '0')}`
    },
    
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    
    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },
    
    getEstadoLabel(status) {
      return this.estadosLabels[status] || status
    },
    
    getEstadoBadgeClass(status) {
      const classes = {
        'draft': 'badge-secondary',
        'generated': 'badge-info',
        'issued': 'badge-success',
        'rejected': 'badge-danger',
        'cancelled': 'badge-dark'
      }
      return classes[status] || 'badge-secondary'
    }
  }
}
</script>

<style scoped>
.historial-ventas {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subtitle {
  margin: 0.5rem 0 0 0;
  color: #7f8c8d;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover {
  background: #2980b9;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover {
  background: #229954;
}

.btn-outline-secondary {
  background: white;
  color: #7f8c8d;
  border: 1px solid #bdc3c7;
}

.btn-outline-secondary:hover {
  background: #ecf0f1;
}

.filters-card {
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 1.5rem;
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  align-items: end;
}

.filter-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #dce0e3;
  border-radius: 0.375rem;
  font-size: 0.9rem;
}

.form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.facturas-table-card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.loading-state,
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
  color: #7f8c8d;
}

.empty-state i {
  font-size: 4rem;
  color: #bdc3c7;
  margin-bottom: 1rem;
}

.table {
  width: 100%;
  margin: 0;
}

.table thead {
  background: #f8f9fa;
}

.table th {
  padding: 1rem;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid #ecf0f1;
  vertical-align: middle;
}

.table tbody tr:hover {
  background: #f8f9fa;
}

.cliente-info {
  display: flex;
  flex-direction: column;
}

.cliente-info small {
  color: #7f8c8d;
  font-size: 0.85rem;
}

.badge {
  padding: 0.35rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-block;
}

.badge-secondary {
  background: #95a5a6;
  color: white;
}

.badge-info {
  background: #3498db;
  color: white;
}

.badge-success {
  background: #27ae60;
  color: white;
}

.badge-danger {
  background: #e74c3c;
  color: white;
}

.badge-dark {
  background: #34495e;
  color: white;
}

.autorizacion-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.factura-numero-link {
  color: #2c3e50;
  text-decoration: none;
  border-bottom: 1px dashed #3498db;
  transition: color 0.2s, border-color 0.2s;
}
.factura-numero-link:hover {
  color: #3498db;
  border-bottom-style: solid;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  justify-content: center;
}

.btn-sm {
  padding: 0.375rem 0.5rem;
  font-size: 0.85rem;
}

.btn-outline-primary {
  background: white;
  color: #3498db;
  border: 1px solid #3498db;
}

.btn-outline-primary:hover {
  background: #3498db;
  color: white;
}

.btn-outline-warning {
  background: white;
  color: #f39c12;
  border: 1px solid #f39c12;
}

.btn-outline-warning:hover {
  background: #f39c12;
  color: white;
}

.btn-outline-success {
  background: white;
  color: #27ae60;
  border: 1px solid #27ae60;
}

.btn-outline-success:hover {
  background: #27ae60;
  color: white;
}

.btn-outline-info {
  background: white;
  color: #3498db;
  border: 1px solid #3498db;
}

.btn-outline-info:hover {
  background: #3498db;
  color: white;
}

.btn-outline-danger {
  background: white;
  color: #e74c3c;
  border: 1px solid #e74c3c;
}

.btn-outline-danger:hover {
  background: #e74c3c;
  color: white;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modales */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 0.5rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-lg {
  max-width: 900px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #ecf0f1;
}

.modal-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
}

.btn-close:hover {
  background: #ecf0f1;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top: 1px solid #ecf0f1;
}

.alert {
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: start;
  gap: 0.75rem;
}

.alert i {
  font-size: 1.2rem;
  margin-top: 0.1rem;
}

.alert-info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.alert-warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.factura-info-box {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.factura-info-box h5 {
  margin-top: 0;
  margin-bottom: 0.75rem;
  color: #2c3e50;
}

.factura-info-box p {
  margin: 0.5rem 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
}

.form-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

/* Detalle Factura */
.factura-detalle {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detalle-section h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  border-bottom: 2px solid #3498db;
  padding-bottom: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  font-size: 0.85rem;
  color: #7f8c8d;
  font-weight: 500;
}

.info-item span,
.info-item strong,
.info-item code {
  font-size: 0.95rem;
  color: #2c3e50;
}

.info-item code {
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  word-break: break-all;
}

.totales-grid {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #dee2e6;
}

.total-row:last-child {
  border-bottom: none;
}

.total-final {
  font-size: 1.2rem;
  font-weight: bold;
  color: #27ae60;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 2px solid #27ae60;
}

@media (max-width: 768px) {
  .historial-ventas {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    align-items: start;
  }
  
  .header-actions {
    width: 100%;
    flex-direction: column;
  }
  
  .filters-row {
    grid-template-columns: 1fr;
  }
  
  .table-responsive {
    overflow-x: auto;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .modal-content {
    max-height: 95vh;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>


